import { useState, useEffect } from 'react';
import { Search, Package, Truck, CheckCircle2, Clock, MapPin, User, Calendar, ExternalLink, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
// import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './TrackOrder.css';

export default function TrackOrder() {
  const { API_BASE } = useAuth();
  const [identifier, setIdentifier] = useState('');
  const [order, setOrder] = useState(null);
  const [liveTracking, setLiveTracking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Auto-track if ID is in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');
    if (id) {
      setIdentifier(id);
      handleTrack(id);
    }
  }, []);

  const handleTrack = async (idToTrack = identifier) => {
    if (!idToTrack) return;
    setLoading(true);
    setError('');
    setLiveTracking(null);
    try {
      // 1. Get internal order details
      const res = await fetch(`${API_BASE}/orders/track/${idToTrack}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Order not found');
      setOrder(data);

      // 2. If AWB exists, get live tracking from Shiprocket
      if (data.awb_code) {
        try {
          const sres = await fetch(`${API_BASE}/shiprocket/track/${data.awb_code}`);
          const sdata = await sres.json();
          
          if (sres.ok && sdata.tracking_data) {
            // Shiprocket structure can vary, try to find activities
            const tracking = sdata.tracking_data;
            const activities = tracking.shipment_track_activities || 
                             (tracking.shipment_track && tracking.shipment_track[0]?.shipment_track_activities);
            
            if (activities) {
              setLiveTracking({
                ...tracking,
                shipment_track_activities: activities
              });
            }
          }
        } catch (serr) {
          console.error("Live tracking fetch failed:", serr);
        }
      }
    } catch (err) {
      setError(err.message || 'Order not found. Please check the ID/Mobile number.');
      setOrder(null);
    } finally {
      setLoading(false);
    }
  };

  const statusIcons = {
    confirmed: <CheckCircle2 size={20} />,
    shipped: <Package size={20} />,
    out_for_delivery: <Truck size={20} />,
    delivered: <MapPin size={20} />,
    cancelled: <AlertCircle size={20} />
  };

  return (
    <main className="track-page container">
      <div className="track-header">
        <span className="eyebrow">Real-time Updates</span>
        <h1>Track Your Order</h1>
        <p>Enter your Order ID or Registered Mobile Number</p>
      </div>

      <div className="track-search-box">
        <div className="search-input-wrap">
          <Search className="search-icon" size={20} />
          <input 
            type="text" 
            placeholder="FLY-123... or 9876543210" 
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleTrack()}
          />
          <button onClick={() => handleTrack()} disabled={loading}>
            {loading ? 'Searching...' : 'Track Now'}
          </button>
        </div>
        {error && <p className="track-error">{error}</p>}
      </div>

      {order && (
        <div className="track-results anim-fade-in">
          <div className="order-main-info">
            <div className="info-card">
              <div className="info-item">
                <Calendar size={16} />
                <div>
                  <p className="label">Order Date</p>
                  <p className="value">{new Date(order.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                </div>
              </div>
              <div className="info-item">
                <Clock size={16} />
                <div>
                  <p className="label">Current Status</p>
                  <p className="value status-pill" data-status={order.status}>
                    {liveTracking?.track_status ? liveTracking.status : order.status.replace(/_/g, ' ')}
                  </p>
                </div>
              </div>
              <div className="info-item">
                <User size={16} />
                <div>
                  <p className="label">Ship to</p>
                  <p className="value">{order.shippingAddress.firstName} {order.shippingAddress.lastName}</p>
                </div>
              </div>
            </div>

            {/* Visual Timeline */}
            <div className="track-timeline-card">
              <h3>
                {liveTracking ? 'Live Tracking Timeline' : 'Tracking Timeline'}
                {order.awb_code && <span className="awb-badge">AWB: {order.awb_code}</span>}
              </h3>
              
              <div className="timeline">
                {liveTracking ? (
                  // SHIPROCKET LIVE TIMELINE
                  liveTracking.shipment_track_activities?.map((activity, idx) => (
                    <div key={idx} className="timeline-item completed">
                      <div className="timeline-icon">
                        {idx === 0 ? <Truck size={20} /> : <div className="timeline-dot" />}
                      </div>
                      <div className="timeline-content">
                        <p className="timeline-status">{activity.status}</p>
                        <p className="timeline-msg">{activity.activity} - {activity.location}</p>
                        <p className="timeline-time">{activity.date}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  // INTERNAL FALLBACK TIMELINE
                  ['confirmed', 'shipped', 'out_for_delivery', 'delivered'].map((step, idx) => {
                    const isCompleted = order.trackingHistory.some(h => h.status === step);
                    const isCurrent = order.status === step;
                    const historyItem = order.trackingHistory.find(h => h.status === step);

                    return (
                      <div key={step} className={`timeline-item ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''}`}>
                        <div className="timeline-icon">
                          {statusIcons[step]}
                        </div>
                        <div className="timeline-content">
                          <p className="timeline-status">{step.replace(/_/g, ' ')}</p>
                          {historyItem && (
                            <>
                              <p className="timeline-msg">{historyItem.message}</p>
                              <p className="timeline-time">{new Date(historyItem.timestamp).toLocaleString()}</p>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          <div className="order-items-summary">
            <h3>Items in Order</h3>
            <div className="track-items-list">
              {order.items.map((item, idx) => (
                <div key={idx} className="track-item">
                  <img src={item.image} alt={item.name} />
                  <div className="item-info">
                    <p className="name">{item.name}</p>
                    <p className="qty">Qty: {item.qty} • ₹{item.price}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="track-order-footer">
              <p>Total Amount: <strong>₹{order.totals.total}</strong></p>
              {liveTracking?.track_url && (
                <a href={liveTracking.track_url} target="_blank" rel="noreferrer" className="admin-btn admin-btn-secondary admin-btn-sm" style={{ marginTop: 10, display: 'inline-flex', gap: 6 }}>
                  View on Shiprocket <ExternalLink size={14} />
                </a>
              )}
              <Link to="/contact" className="help-link">Need Help? Contact Support <ExternalLink size={14} /></Link>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
